/**
 * residentTaxEngine.ts
 *
 * 【役割】個人住民税（道府県民税 + 市町村民税）の税額計算エンジン
 *
 * 【所得税エンジンとの関係】
 *   - 所得金額の計算は incomeEngine.ts を共有（コードの重複なし）
 *   - 青色申告特別控除の適用ロジック（不動産→事業→山林のバトンパス）も共有
 *   - 税率・所得控除の定数のみ住民税固有の値に置き換える
 *
 * 【住民税固有の仕様】
 *   - 所得割: 総合課税・山林 10%（道府県4%+市町村6%）、不動産長期譲渡 5%、不動産短期譲渡 9%
 *   - 山林所得に5分5乗方式は適用しない（所得税のみの特例）
 *   - 基礎控除: 43万円（合計所得金額により逓減あり）
 *   - 調整控除: 人的控除の所得税・住民税間の差額を補正する税額控除
 *   - 住宅ローン控除: 所得税で引ききれなかった残額のみを控除
 *   - 均等割: 5,000円（道府県1,000円 + 市町村3,000円 + 森林環境税1,000円）
 *
 * 【将来追加予定のエンジン】
 *   - socialInsuranceEngine.ts（社会保険料）も incomeEngine を同様に呼び出す想定
 */

import * as incomeEngine from './incomeEngine';
import * as taxDeducCalc from './taxDeducCalc';
import type { 全収入情報, 住民税計算入力, 住民税人的控除情報 } from './taxCalcInput';

// =============================================================================
// 定数（マジックナンバー禁止）
// =============================================================================

/**
 * 住民税の税率・控除額・各種定数をまとめたオブジェクト。
 * `as const` で値を固定し、誤書き換えを防ぐ。
 */
const 住民税定数 = {
  // --- 所得割 税率 ---
  税率: {
    /** 総合課税（給与・事業・不動産・一時・雑・不動産以外譲渡） */
    総合課税: 0.10,
    /** 山林所得（分離課税・5分5乗なし） */
    山林所得: 0.10,
    /** 不動産の長期譲渡所得（所有5年超）: 道府県1.6% + 市町村3.4% */
    不動産長期譲渡: 0.05,
    /** 不動産の短期譲渡所得（所有5年以下）: 道府県3% + 市町村6% */
    不動産短期譲渡: 0.09,
  },

  // --- 基礎控除（合計所得金額に応じて逓減） ---
  基礎控除: {
    通常額: 430_000,   // 合計所得 2,400万以下
    逓減額1: 290_000,  // 2,400万超〜2,450万以下
    逓減額2: 150_000,  // 2,450万超〜2,500万以下
    逓減額3: 0,        // 2,500万超
    境界所得1: 24_000_000,
    境界所得2: 24_500_000,
    境界所得3: 25_000_000,
  },

  // --- その他の所得控除額 ---
  控除額: {
    勤労学生: 260_000,        // 所得税27万と5千円異なる
    一般配偶者: 330_000,      // 所得税38万と異なる
    老人配偶者: 380_000,      // 70歳以上配偶者。所得税48万と異なる
    一般扶養: 330_000,        // 所得税38万と異なる
    特定扶養: 450_000,        // 19〜22歳。所得税63万と異なる
    老人扶養: 380_000,        // 70歳以上。所得税48万と異なる
    生命保険料上限: 70_000,   // 所得税12万（一般・介護・個人年金計）と計算方式が異なる
  },

  /** 勤労学生控除の合計所得金額の上限（所得税89万とは異なる） */
  勤労学生所得上限: 750_000,

  // --- 調整控除（所得税との人的控除差額を補正）---
  調整控除: {
    /**
     * 人的控除の差額（所得税控除額 - 住民税控除額）。
     * 基礎控除の差額は5万円として固定する（令和7年度税制改正後の想定）。
     */
    基礎控除差額: 50_000,      // 所得税48万 - 住民税43万
    勤労学生差額: 10_000,      // 所得税27万 - 住民税26万
    一般配偶者差額: 50_000,    // 所得税38万 - 住民税33万
    老人配偶者差額: 100_000,   // 所得税48万 - 住民税38万
    一般扶養差額: 50_000,      // 所得税38万 - 住民税33万
    特定扶養差額: 180_000,     // 所得税63万 - 住民税45万
    老人扶養差額: 100_000,     // 所得税48万 - 住民税38万
    /** 調整控除の計算税率（固定） */
    税率: 0.05,
    /** 合計課税所得金額がこの金額以下なら「低所得者向け計算式」を適用 */
    境界所得: 2_000_000,
    /** 合計課税所得金額が200万超の場合に保証される調整控除の最低額 */
    最低額: 2_500,
  },

  // --- 住宅ローン控除（住民税分）---
  住宅ローン控除: {
    /** 所得税の課税総所得金額等に対する控除上限の割合 */
    上限率: 0.05,
    /** 上限の絶対値 */
    最大額: 97_500,
  },

  /** 均等割: 道府県民税1,000円 + 市民税3,000円 + 森林環境税1,000円 */
  均等割: 5_000,
} as const;

// =============================================================================
// 内部型（モジュール外には公開しない）
// =============================================================================

/** incomeEngine の呼び出し結果をまとめた中間データ */
type 所得中間結果 = {
  給与所得: number;
  一時所得: number;
  雑所得: number;
  不動産所得: number;
  事業所得: number;
  不動産以外の譲渡所得: number;
  山林所得: number;
  不動産の譲渡所得: number;
  /** 総合課税対象の所得合計（一時所得は 1/2 で算入） */
  総所得: number;
  /** 全所得種別の合計（基礎控除の逓減判定・調整控除の人的控除差額計算に使用） */
  合計所得金額: number;
  /** 不動産→事業と青色控除枠を消費した残り（山林所得の控除枠バトンパス用） */
  不動産所得と事業所得を考慮後の青色申告控除額: number;
};

// =============================================================================
// 内部ヘルパー関数（モジュール外には公開しない）
// =============================================================================

/**
 * incomeEngine を呼び出して全所得種別の金額を一括計算する。
 * nationalTaxEngine と全く同じ順序・ロジックで所得を確定させる。
 */
function 全所得計算(所得計算入力: 全収入情報, 青色申告控除額: number): 所得中間結果 {
  const 給与所得 = incomeEngine.給与所得計算関数(所得計算入力.給与収入);

  const 一時所得 = 所得計算入力.一時所得計算のための情報
    ? incomeEngine.一時所得計算関数(所得計算入力.一時所得計算のための情報)
    : 0;

  const 不動産以外の譲渡所得 = 所得計算入力.不動産以外の譲渡所得計算のための情報
    ? incomeEngine.不動産以外の譲渡所得計算関数(
        所得計算入力.不動産以外の譲渡所得計算のための情報,
      )
    : 0;

  const 雑所得 = 所得計算入力.雑所得計算のための情報
    ? incomeEngine.雑所得計算関数(所得計算入力.雑所得計算のための情報, 所得計算入力.年齢)
    : 0;

  // 青色控除枠: 不動産 → 事業 の順に消費
  const { 不動産所得, 不動産所得を考慮後の青色申告控除額 } =
    所得計算入力.不動産所得計算のための情報
      ? incomeEngine.不動産所得計算関数(
          所得計算入力.不動産所得計算のための情報,
          青色申告控除額,
        )
      : { 不動産所得: 0, 不動産所得を考慮後の青色申告控除額: 青色申告控除額 };

  const { 事業所得, 不動産所得と事業所得を考慮後の青色申告控除額 } =
    所得計算入力.事業所得計算のための情報
      ? incomeEngine.事業所得計算関数(
          所得計算入力.事業所得計算のための情報,
          不動産所得を考慮後の青色申告控除額,
        )
      : { 事業所得: 0, 不動産所得と事業所得を考慮後の青色申告控除額: 青色申告控除額 };

  // 山林: 青色控除残枠を消費（住民税でも特別控除50万+青色控除を所得計算に含める）
  const { 山林所得 } = 所得計算入力.山林所得計算のための情報
    ? incomeEngine.山林所得計算関数(
        所得計算入力.山林所得計算のための情報,
        不動産所得と事業所得を考慮後の青色申告控除額,
      )
    : { 山林所得: 0 };

  const { 不動産の譲渡所得 } = 所得計算入力.不動産の譲渡所得計算のための情報
    ? incomeEngine.不動産の譲渡所得計算関数(所得計算入力.不動産の譲渡所得計算のための情報)
    : { 不動産の譲渡所得: 0 };

  // 一時所得は総所得算入時に 1/2（所得税と同じルール）
  const 総所得 =
    給与所得 + 一時所得 / 2 + 雑所得 + 不動産所得 + 事業所得 + 不動産以外の譲渡所得;
  const 合計所得金額 = 総所得 + 山林所得 + 不動産の譲渡所得;

  return {
    給与所得,
    一時所得,
    雑所得,
    不動産所得,
    事業所得,
    不動産以外の譲渡所得,
    山林所得,
    不動産の譲渡所得,
    総所得,
    合計所得金額,
    不動産所得と事業所得を考慮後の青色申告控除額,
  };
}

/** 合計所得金額に基づいて住民税の基礎控除額を返す（逓減あり） */
function 住民税基礎控除額計算(合計所得金額: number): number {
  const { 基礎控除 } = 住民税定数;
  if (合計所得金額 <= 基礎控除.境界所得1) return 基礎控除.通常額;
  if (合計所得金額 <= 基礎控除.境界所得2) return 基礎控除.逓減額1;
  if (合計所得金額 <= 基礎控除.境界所得3) return 基礎控除.逓減額2;
  return 基礎控除.逓減額3;
}

/** 住民税の全所得控除を計算し、合計額と内訳を返す */
function 所得控除計算(
  人的控除情報: 住民税人的控除情報,
  学生かどうか: boolean,
  合計所得金額: number,
): {
  合計: number;
  内訳: {
    基礎控除: number;
    勤労学生控除: number;
    配偶者控除: number;
    扶養控除: number;
    生命保険料控除: number;
  };
} {
  const { 控除額, 勤労学生所得上限 } = 住民税定数;

  const 基礎控除 = 住民税基礎控除額計算(合計所得金額);

  // 住民税の勤労学生控除は合計所得75万円以下が条件（所得税の89万とは異なる）
  const 勤労学生控除 =
    学生かどうか && 合計所得金額 <= 勤労学生所得上限 ? 控除額.勤労学生 : 0;

  // 老人（70歳以上）が優先。両方trueは想定しないが老人を先に判定
  const 配偶者控除 = 人的控除情報.老人配偶者控除対象か
    ? 控除額.老人配偶者
    : 人的控除情報.一般配偶者控除対象か
      ? 控除額.一般配偶者
      : 0;

  const 扶養控除 =
    控除額.一般扶養 * 人的控除情報.一般扶養親族数 +
    控除額.特定扶養 * 人的控除情報.特定扶養親族数 +
    控除額.老人扶養 * 人的控除情報.老人扶養親族数;

  const 生命保険料控除 = Math.min(
    人的控除情報.生命保険料控除対象額,
    控除額.生命保険料上限,
  );

  const 合計 = 基礎控除 + 勤労学生控除 + 配偶者控除 + 扶養控除 + 生命保険料控除;

  return { 合計, 内訳: { 基礎控除, 勤労学生控除, 配偶者控除, 扶養控除, 生命保険料控除 } };
}

/**
 * 調整控除の計算に使う「人的控除の差額合計」を計算する。
 * 差額 = 所得税の控除額 - 住民税の控除額
 */
function 人的控除差額合計計算(
  人的控除情報: 住民税人的控除情報,
  学生かどうか: boolean,
  合計所得金額: number,
): number {
  const { 調整控除, 勤労学生所得上限 } = 住民税定数;

  // 基礎控除の差額は常に加算（全員対象）
  let 差額合計 = 調整控除.基礎控除差額;

  if (学生かどうか && 合計所得金額 <= 勤労学生所得上限) {
    差額合計 += 調整控除.勤労学生差額;
  }

  if (人的控除情報.老人配偶者控除対象か) {
    差額合計 += 調整控除.老人配偶者差額;
  } else if (人的控除情報.一般配偶者控除対象か) {
    差額合計 += 調整控除.一般配偶者差額;
  }

  差額合計 += 調整控除.一般扶養差額 * 人的控除情報.一般扶養親族数;
  差額合計 += 調整控除.特定扶養差額 * 人的控除情報.特定扶養親族数;
  差額合計 += 調整控除.老人扶養差額 * 人的控除情報.老人扶養親族数;

  return 差額合計;
}

/**
 * 調整控除額を計算する。
 *
 * 合計課税所得 ≤ 200万:
 *   Min(人的控除差額合計, 合計課税所得) × 5%
 *
 * 合計課税所得 > 200万:
 *   {人的控除差額合計 - (合計課税所得 - 200万)} × 5%
 *   ただし計算結果が 2,500 円未満のときは 2,500 円
 */
function 調整控除計算(人的控除差額合計: number, 合計課税所得: number): number {
  const { 調整控除 } = 住民税定数;

  if (合計課税所得 <= 調整控除.境界所得) {
    // 200万以下: 差額合計と課税所得のうち小さい方 × 5%
    return Math.min(人的控除差額合計, 合計課税所得) * 調整控除.税率;
  }

  // 200万超: 差額合計 - (課税所得超過分) の 5%、最低2,500円を保証
  const 計算値 = (人的控除差額合計 - (合計課税所得 - 調整控除.境界所得)) * 調整控除.税率;
  return Math.max(調整控除.最低額, 計算値);
}

// =============================================================================
// 公開型定義
// =============================================================================

/** 住民税の所得控除内訳 */
export type 住民税所得控除内訳 = {
  基礎控除: number;
  勤労学生控除: number;
  配偶者控除: number;
  扶養控除: number;
  生命保険料控除: number;
};

/** 住民税・総合課税の計算結果 */
export type 住民税総合課税計算結果 = {
  給与所得: number;
  一時所得: number;
  雑所得: number;
  不動産所得: number;
  事業所得: number;
  不動産以外の譲渡所得: number;
  総所得: number;
  所得控除内訳: 住民税所得控除内訳;
  所得控除合計: number;
  課税総所得: number;
  /** 課税総所得 × 10% */
  総合課税所得割: number;
};

/** 住民税・山林所得の計算結果（5分5乗なし） */
export type 住民税山林所得計算結果 = {
  山林所得: number;
  /** 住民税では1/5しない（所得税のみの特例）。山林所得がそのまま課税ベース */
  課税山林所得: number;
  /** 課税山林所得 × 10% */
  山林所得割: number;
};

/** 住民税・不動産の譲渡所得の計算結果 */
export type 住民税不動産譲渡計算結果 = {
  不動産の譲渡所得: number;
  /** 長期5% / 短期9% の適用後税額 */
  不動産の譲渡所得割: number;
  /** 実際に適用した税率（長期: 0.05 / 短期: 0.09） */
  適用税率: number;
};

/** 住民税・納付税額計算の最終結果 */
export type 住民税納付額計算結果 = {
  /** 総合課税（給与・事業・不動産・一時・雑・不動産以外譲渡）の計算結果 */
  総合課税結果: 住民税総合課税計算結果;
  /** 山林所得（分離課税）の計算結果 */
  山林所得結果: 住民税山林所得計算結果;
  /** 不動産の譲渡所得（分離課税）の計算結果 */
  不動産の譲渡所得結果: 住民税不動産譲渡計算結果;
  /** 全所得種別の合計（基礎控除の逓減・調整控除の算定基準） */
  合計所得金額: number;
  /** 所得割の合計（調整控除・住宅控除適用前） */
  所得割合計: number;
  /** 調整控除額（人的控除の所得税・住民税間の差を補正） */
  調整控除額: number;
  /** 住宅ローン控除の適用額（所得税の残額を住民税から控除） */
  住宅ローン控除適用額: number;
  /** 均等割（5,000円・固定） */
  均等割: number;
  /** 最終的な住民税の納付税額（所得割 - 調整控除 - 住宅控除 + 均等割） */
  住民税納付税額: number;
};

// =============================================================================
// 公開関数
// =============================================================================

/**
 * 住民税総合課税額計算関数
 *
 * 総合課税対象（給与・一時・雑・不動産・事業・不動産以外譲渡）の
 * 住民税所得割を計算する。山林・不動産譲渡の税額は含まない。
 *
 * 返り値には、山林所得計算のバトンパスに必要な青色控除残額と、
 * 住民税納付税額計算関数が必要とする合計所得金額も含む。
 *
 * @param 所得計算入力 - 全収入情報（incomeEngine と共有）
 * @param 人的控除情報 - 住民税固有の人的控除情報
 * @param 青色申告控除額 - taxDeducCalc.青色申告特別控除額計算関数 の戻り値
 */
export function 住民税総合課税額計算関数(
  所得計算入力: 全収入情報,
  人的控除情報: 住民税人的控除情報,
  青色申告控除額: number,
): {
  住民税総合課税結果: 住民税総合課税計算結果;
  /** 基礎控除逓減の判定・調整控除の算定に使う全所得合計 */
  合計所得金額: number;
  /** 山林所得の青色控除バトンパス用（住民税納付税額計算関数 に渡す） */
  不動産所得と事業所得を考慮後の青色申告控除額: number;
} {
  const 所得 = 全所得計算(所得計算入力, 青色申告控除額);

  const { 合計, 内訳 } = 所得控除計算(
    人的控除情報,
    所得計算入力.学生かどうか,
    所得.合計所得金額,
  );

  const 課税総所得 = Math.max(0, 所得.総所得 - 合計);
  const 総合課税所得割 = 課税総所得 * 住民税定数.税率.総合課税;

  return {
    住民税総合課税結果: {
      給与所得: 所得.給与所得,
      一時所得: 所得.一時所得,
      雑所得: 所得.雑所得,
      不動産所得: 所得.不動産所得,
      事業所得: 所得.事業所得,
      不動産以外の譲渡所得: 所得.不動産以外の譲渡所得,
      総所得: 所得.総所得,
      所得控除内訳: 内訳,
      所得控除合計: 合計,
      課税総所得,
      総合課税所得割,
    },
    合計所得金額: 所得.合計所得金額,
    不動産所得と事業所得を考慮後の青色申告控除額:
      所得.不動産所得と事業所得を考慮後の青色申告控除額,
  };
}

/**
 * 住民税納付税額計算関数
 *
 * 全所得種別・全控除・調整控除・住宅ローン控除（残額）・均等割を含む
 * 住民税の試算を行い、最終的な納付税額を返す。
 *
 * 計算フロー:
 *   1. 青色申告特別控除額の決定
 *   2. 総合課税部分（住民税総合課税額計算関数 を呼び出す）
 *   3. 山林所得の所得割（5分5乗なし・フラット10%）
 *   4. 不動産の譲渡所得の所得割（長期5% / 短期9%）
 *   5. 調整控除（人的控除の差額 × 5%）
 *   6. 住宅ローン控除残額の適用（上限: 所得税課税総所得等×5%、最大97,500円）
 *   7. 均等割（5,000円）の加算
 *
 * @param input - 住民税計算入力（所得情報 + 人的控除情報 + 所得税サマリ）
 */
export function 住民税納付税額計算関数(input: 住民税計算入力): 住民税納付額計算結果 {
  const { 所得計算入力, 人的控除情報, 所得税サマリ } = input;

  // 1. 青色申告特別控除額（所得税と同じ計算式を流用）
  const 青色申告控除額 = 所得計算入力.青色申告情報
    ? taxDeducCalc.青色申告特別控除額計算関数(所得計算入力.青色申告情報)
    : 0;

  // 2. 総合課税
  const { 住民税総合課税結果, 合計所得金額, 不動産所得と事業所得を考慮後の青色申告控除額 } =
    住民税総合課税額計算関数(所得計算入力, 人的控除情報, 青色申告控除額);

  // 3. 山林所得（分離課税・住民税では5分5乗なし）
  const 山林所得結果 = ((): 住民税山林所得計算結果 => {
    if (!所得計算入力.山林所得計算のための情報) {
      return { 山林所得: 0, 課税山林所得: 0, 山林所得割: 0 };
    }
    const { 山林所得 } = incomeEngine.山林所得計算関数(
      所得計算入力.山林所得計算のための情報,
      不動産所得と事業所得を考慮後の青色申告控除額,
    );
    const 課税山林所得 = Math.max(0, 山林所得);
    return {
      山林所得,
      課税山林所得,
      山林所得割: 課税山林所得 * 住民税定数.税率.山林所得,
    };
  })();

  // 4. 不動産の譲渡所得（長期5% / 短期9%）
  const 不動産の譲渡所得結果 = ((): 住民税不動産譲渡計算結果 => {
    if (!所得計算入力.不動産の譲渡所得計算のための情報) {
      return { 不動産の譲渡所得: 0, 不動産の譲渡所得割: 0, 適用税率: 0 };
    }
    const { 不動産の譲渡所得 } = incomeEngine.不動産の譲渡所得計算関数(
      所得計算入力.不動産の譲渡所得計算のための情報,
    );
    const 適用税率 = 所得計算入力.不動産の譲渡所得計算のための情報.所有期間は5年超か
      ? 住民税定数.税率.不動産長期譲渡
      : 住民税定数.税率.不動産短期譲渡;
    return {
      不動産の譲渡所得,
      不動産の譲渡所得割: 不動産の譲渡所得 * 適用税率,
      適用税率,
    };
  })();

  // 5. 所得割合計（調整控除・住宅控除適用前）
  const 所得割合計 =
    住民税総合課税結果.総合課税所得割 +
    山林所得結果.山林所得割 +
    不動産の譲渡所得結果.不動産の譲渡所得割;

  // 6. 調整控除
  const 合計課税所得 =
    住民税総合課税結果.課税総所得 +
    山林所得結果.課税山林所得 +
    不動産の譲渡所得結果.不動産の譲渡所得;

  const 人的控除差額合計 = 人的控除差額合計計算(
    人的控除情報,
    所得計算入力.学生かどうか,
    合計所得金額,
  );
  const 調整控除額 = 調整控除計算(人的控除差額合計, 合計課税所得);

  // 7. 住宅ローン控除残額（所得税で引ききれなかった分）
  // 上限 = min(所得税の課税総所得等 × 5%, 97,500円)
  const 住宅ローン控除上限額 = Math.min(
    所得税サマリ.所得税の課税総所得金額等合計 * 住民税定数.住宅ローン控除.上限率,
    住民税定数.住宅ローン控除.最大額,
  );
  const 住宅ローン控除適用額 = Math.min(
    所得税サマリ.所得税で引ききれなかった住宅ローン控除残額,
    住宅ローン控除上限額,
  );

  // 8. 最終納付税額 = max(0, 所得割合計 - 調整控除 - 住宅控除) + 均等割
  const 所得割最終 = Math.max(0, 所得割合計 - 調整控除額 - 住宅ローン控除適用額);
  const 住民税納付税額 = 所得割最終 + 住民税定数.均等割;

  return {
    総合課税結果: 住民税総合課税結果,
    山林所得結果,
    不動産の譲渡所得結果,
    合計所得金額,
    所得割合計,
    調整控除額,
    住宅ローン控除適用額,
    均等割: 住民税定数.均等割,
    住民税納付税額,
  };
}
